// used in datatable
export const MILLIS_TO_LOCALE = (millis: number, withSeconds = false) => {
    const date = new Date(millis);
    const options: any = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        ...(withSeconds ? { second: "2-digit" } : {}),
        hour12: true,
    };
    return (millis && date.toLocaleString("en-US", options)) || "-";
};

const AVAILS_PACIFIC_TZ = "America/Los_Angeles";

export type LicenseWindowStatus = "past" | "current" | "future";

function compareCalendarDates(a: string, b: string): number {
    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;
    return a.localeCompare(b);
}

/** Pacific calendar date (YYYY-MM-DD) from a stored catalog instant or legacy date string. */
export function pacificCalendarDateFromAvailsValue(
    value: string | number | undefined | null
): string {
    if (value === null || value === undefined || value === "") return "";
    const trimmed = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = parseAvailsDateValue(value);
    if (!parsed) return "";
    return parsed.toLocaleDateString("en-CA", { timeZone: AVAILS_PACIFIC_TZ });
}

/** Match backend classifyLicenseWindowStatus for catalog list/detail rows. */
export function classifyAvailsLicenseWindowStatus(
    startValue: string | number | undefined | null,
    endValue: string | number | undefined | null
): LicenseWindowStatus {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: AVAILS_PACIFIC_TZ });
    const start = pacificCalendarDateFromAvailsValue(startValue);
    const end = pacificCalendarDateFromAvailsValue(endValue);
    if (!start || !end) return "future";
    if (compareCalendarDates(end, today) < 0) return "past";
    if (compareCalendarDates(start, today) <= 0 && compareCalendarDates(end, today) >= 0) {
        return "current";
    }
    if (compareCalendarDates(start, today) > 0) return "future";
    return "past";
}

export function parseAvailsDateValue(
    value: string | number | undefined | null
): Date | null {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number" && Number.isFinite(value)) {
        return new Date(value);
    }
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return new Date(`${trimmed}T12:00:00Z`);
    }
    if (/^\d+$/.test(trimmed)) {
        const millis = Number(trimmed);
        if (Number.isFinite(millis)) return new Date(millis);
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Avails license windows are Pacific-local; APIs return UTC ISO instants or millis. */
export function formatAvailsPacificDateTime(
    value: string | number | undefined | null
): string {
    const parsed = parseAvailsDateValue(value);
    if (!parsed) {
        if (value === null || value === undefined || value === "") return "-";
        const fallback = String(value).trim();
        return fallback || "-";
    }
    return parsed.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: AVAILS_PACIFIC_TZ,
        timeZoneName: "short",
    });
}

// used in datatable
export const MILLIS_TO_LOCALE_DIGIT_FORMAT = (millis: number) => {
    const options: any = {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    };
    const formattedDate = new Date(millis).toLocaleString("en-US", options);
    return formattedDate;
};

// used in schedule popup message
export const MILLIS_TO_LOCALE_DATE_TIME_FORMAT_POPUP = (datetime: string) => {
    const dateString = new Date(datetime).toLocaleString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    return dateString;
};

// used in realtime tooltip
export const LOCAL_PST_DATE_CONVERSION = (
    dateFormat: string,
    dateTime: string
) => {
    if (!dateFormat) return;
    const datetime = mergeDateTime(dateFormat, dateTime);
    if (datetime) {
        const dateString = new Date(datetime).toLocaleString("en-US", {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
        });
        return dateString;
    } else {
        return "";
    }
};

export const LOCAL_DATE_CONVERSION = (datetime: string) =>
    new Date(datetime).toLocaleString("en-US", {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
    });

// finding difference in dates
export const dateDifference = (date: any) => {
    const date1: any = new Date();
    const date2: any = new Date(date);
    const diffTime: any = Math.abs(date2 - date1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

// used for merging date & time
export const mergeDateTime = (date: any, datetime: any) => {
    if (!datetime) datetime = new Date();
    console.log(date, datetime);
    const actualDate = new Date(date);
    const actualDateTime = new Date(datetime);
    actualDate.setHours(actualDateTime.getHours());
    actualDate.setMinutes(actualDateTime.getMinutes());
    actualDate.setSeconds(actualDateTime.getSeconds());
    return actualDate;
};
